/**
 * Small pieces shared by the Funder interfaces views: badges, buttons, the
 * note form every reject / dismiss / expire requires, and the JSON value
 * editor used wherever a person corrects a rule. Styled with the app tokens
 * (ink, paper, v2border, v2accent) like the rest of the WeaveIn admin.
 */
import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { RunStatus, Severity } from '../../../shared/services/funderAgentsApi';

export type Notify = (ok: boolean, text: string) => void;

export const GATE_COPY = 'Agents draft and check. A person approves every change.';

export function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
}

export function fmtDay(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString();
}

export function duration(start?: string | null, end?: string | null): string {
  if (!start) return '—';
  const ms = (end ? new Date(end).getTime() : Date.now()) - new Date(start).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

export function showValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}

const SEVERITY: Record<Severity, string> = {
  high: 'bg-danger-soft text-danger-ink border-danger',
  medium: 'bg-attention-soft text-attention-ink border-attention',
  low: 'bg-paper-2 text-ink-500 border-v2border',
};

export function SeverityBadge({ severity }: { severity: Severity }) {
  return (
    <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full border ${SEVERITY[severity] ?? SEVERITY.low}`}>
      {severity}
    </span>
  );
}

const RUN: Record<string, string> = {
  queued: 'bg-paper-2 text-ink-500 border-v2border',
  running: 'bg-v2accent-soft text-v2accent-ink border-v2accent',
  completed: 'bg-success-soft text-success-ink border-success',
  failed: 'bg-danger-soft text-danger-ink border-danger',
};

export function RunBadge({ status }: { status: RunStatus | string }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${RUN[status] ?? RUN.queued}`}>
      {status === 'running' && <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />}
      {status}
    </span>
  );
}

export function Btn({
  children,
  onClick,
  disabled,
  busy,
  tone = 'secondary',
  title,
  type = 'button',
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  busy?: boolean;
  tone?: 'primary' | 'secondary' | 'danger' | 'ghost';
  title?: string;
  type?: 'button' | 'submit';
}) {
  const cls =
    tone === 'primary'
      ? 'bg-ink text-paper hover:bg-ink-700 border-ink'
      : tone === 'danger'
        ? 'bg-surface text-danger-ink border-danger hover:bg-danger-soft'
        : tone === 'ghost'
          ? 'bg-transparent text-ink-700 border-transparent hover:bg-paper-2'
          : 'bg-surface text-ink border-v2border hover:bg-paper-2';
  return (
    <button
      type={type}
      title={title}
      onClick={onClick}
      disabled={disabled || busy}
      className={`inline-flex items-center gap-1.5 text-[13px] font-semibold px-3 py-1.5 rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${cls}`}
    >
      {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />}
      {children}
    </button>
  );
}

/** A reason a person must give (reject, dismiss, expire). Submit stays disabled until it has text. */
export function NoteForm({
  label,
  placeholder,
  submitLabel,
  tone = 'danger',
  onSubmit,
  onCancel,
}: {
  label: string;
  placeholder?: string;
  submitLabel: string;
  tone?: 'danger' | 'primary';
  onSubmit: (note: string) => Promise<void> | void;
  onCancel: () => void;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="mt-2 rounded-md border border-v2border bg-paper p-3 space-y-2"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!note.trim()) return;
        setBusy(true);
        try {
          await onSubmit(note.trim());
        } finally {
          setBusy(false);
        }
      }}
    >
      <label className="block text-xs font-semibold text-ink-700">
        {label}
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={placeholder}
          rows={2}
          className="mt-1 w-full rounded-md border border-v2border bg-surface px-2 py-1.5 text-sm text-ink"
        />
      </label>
      <div className="flex gap-2">
        <Btn type="submit" tone={tone} disabled={!note.trim()} busy={busy}>
          {submitLabel}
        </Btn>
        <Btn tone="ghost" onClick={onCancel}>
          Cancel
        </Btn>
      </div>
    </form>
  );
}

/** Edit a rule value as JSON (lists, booleans, numbers, objects all round-trip). */
export function ValueEditor({
  initial,
  submitLabel,
  requireNote = true,
  onSubmit,
  onCancel,
}: {
  initial: unknown;
  submitLabel: string;
  requireNote?: boolean;
  onSubmit: (value: unknown, note: string) => Promise<void> | void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(() => JSON.stringify(initial ?? null));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  let parsed: unknown;
  let parseError: string | null = null;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    parseError = 'Not valid JSON — e.g. ["U1"], true, 90 or {"G0022": "G0019"}';
  }
  const ready = !parseError && (!requireNote || note.trim().length > 0);
  return (
    <div className="mt-2 rounded-md border border-v2border bg-paper p-3 space-y-2">
      <label className="block text-xs font-semibold text-ink-700">
        New value (JSON)
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="mt-1 w-full rounded-md border border-v2border bg-surface px-2 py-1.5 font-mono text-sm text-ink"
        />
      </label>
      {parseError && <p className="text-xs text-danger-ink">{parseError}</p>}
      {requireNote && (
        <label className="block text-xs font-semibold text-ink-700">
          Why (kept with the change)
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Manual §4.2 requires U2 for telephonic visits"
            className="mt-1 w-full rounded-md border border-v2border bg-surface px-2 py-1.5 text-sm text-ink"
          />
        </label>
      )}
      <div className="flex gap-2">
        <Btn
          tone="primary"
          disabled={!ready}
          busy={busy}
          onClick={async () => {
            if (!ready) return;
            setBusy(true);
            try {
              await onSubmit(parsed, note.trim());
            } finally {
              setBusy(false);
            }
          }}
        >
          {submitLabel}
        </Btn>
        <Btn tone="ghost" onClick={onCancel}>
          Cancel
        </Btn>
      </div>
    </div>
  );
}

export function Empty({ children }: { children: React.ReactNode }) {
  return <div className="rounded-md border border-dashed border-v2border bg-paper p-6 text-sm text-ink-500 text-center">{children}</div>;
}

export function ErrorLine({ text }: { text: string | null }) {
  if (!text) return null;
  return <p className="text-sm text-danger-ink">{text}</p>;
}

export const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
